/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================
* (Your original copyright and license info)
=========================================================
*/

import { useEffect, useState, useContext, useMemo } from "react"; // MODIFIED: Added useContext, useMemo
import { useLocation, NavLink, useNavigate } from "react-router-dom"; // MODIFIED: Added useNavigate

import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

// --- NEW IMPORTS ---
import { AuthContext } from "context/authContext"; // <-- Adjust path if needed
import routes from "routes"; // <-- Import the static routes array (adjust path if needed)
import MDAvatar from "components/MDAvatar"; // Assuming MDAvatar is used for profile icon
const defaultProfilePictureFromAssets = "assets/images/team-3.jpg"; // Or your actual default image path for user avatar
// --- END NEW IMPORTS ---

// MODIFIED: Removed 'routes' from props
function Sidenav({ color, brand, brandName, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;

  // --- NEW: Get AuthContext data ---
  const authContext = useContext(AuthContext);
  const { isAuthenticated, userRole, userInfo, logout, isLoading: authIsLoading } = authContext;
  const navigate = useNavigate();
  // --- END NEW ---

  // --- NEW: Filter the imported 'routes' array based on userRole ---
  const routesToDisplay = useMemo(() => {
    if (!isAuthenticated || !userRole) {
      return [];
    }

    const originalRoutes = routes;

    if (userRole === "SuperAdmin") {
      return originalRoutes
        .filter((route) => route.key !== "scans")
        .map((route) => {
          const newRoute = { ...route };
          if (route.collapse) {
            newRoute.collapse = route.collapse.map((subItem) => ({ ...subItem }));
          }
          return newRoute;
        });
    }

    // Filtering for other roles (Revendeur, Producer)
    const filtered = [];
    originalRoutes.forEach((route) => {
      let showTopLevelRoute = false;
      let processedRoute = { ...route };

      if (userRole === "Revendeur") {
        if (route.key === "website_Reservations") {
          showTopLevelRoute = true;
          if (route.collapse) {
            processedRoute.collapse = route.collapse.map((subItem) => ({ ...subItem }));
          } else {
            processedRoute.collapse = [];
          }
        }
      } else if (userRole === "Producer") {
        if (
          route.key === "Event" ||
          route.key === "website_Reservations" ||
          route.key === "website_Coupons" ||
          route.key === "website_Revendeurs"
          // route.key === "scans"
        ) {
          showTopLevelRoute = true;
          if (route.collapse) {
            processedRoute.collapse = route.collapse.map((sub) => ({ ...sub }));
          } else {
            processedRoute.collapse = [];
          }
        }
      }

      if (route.key === "brooklyn-alice") {
        processedRoute.name = `${
          userInfo?.fullName || userInfo?.email || "Mon Compte"
        } (${userRole})`;
        processedRoute.icon = (
          <MDAvatar
            src={userInfo?.profilePicture || defaultProfilePictureFromAssets}
            alt="User"
            size="sm"
          />
        );

        if (route.collapse) {
          const filteredCollapse = route.collapse.filter((sub) => sub.key === "logout");
          processedRoute.collapse = filteredCollapse.map((sub) => ({ ...sub }));
        } else {
          processedRoute.collapse = [];
        }
        showTopLevelRoute = true;
      }

      if (route.type === "divider" && route.key === "divider-0") {
        showTopLevelRoute = true;
      }

      if (showTopLevelRoute) {
        if (
          processedRoute.route ||
          processedRoute.href ||
          (processedRoute.collapse && processedRoute.collapse.length > 0) ||
          processedRoute.type === "divider" ||
          processedRoute.type === "title"
        ) {
          filtered.push(processedRoute);
        }
      }
    });
    return filtered;
  }, [isAuthenticated, userRole, userInfo, routes]);

  const currentItems = pathname.split("/").slice(1);
  const collapseName =
    routesToDisplay.length > 0 && currentItems.length > 0 ? currentItems[0] : false;
  const itemParentName =
    routesToDisplay.length > 0 && currentItems.length > 1 ? currentItems[1] : false;
  const itemName =
    routesToDisplay.length > 0 && currentItems.length > 0
      ? currentItems[currentItems.length - 1]
      : false;

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    if (collapseName) setOpenCollapse(collapseName);
    if (itemParentName) setOpenNestedCollapse(itemParentName);
  }, [pathname, collapseName, itemParentName]);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  const renderNestedCollapse = (collapseItems) => {
    if (!collapseItems) return null;
    const template = collapseItems.map(({ name, route, key, href, icon }) => {
      // Added icon
      if (key === "logout") {
        return (
          <SidenavItem
            key={key}
            name={name}
            icon={icon} // Use icon if defined in routes.js for logout
            nested
            onClick={() => {
              if (logout && typeof logout === "function") logout();
              navigate("/authentication/sign-in/basic"); // Adjust to your sign-in route
            }}
          />
        );
      }
      return href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} icon={icon} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem
            name={name}
            icon={icon}
            active={route === pathname || pathname.startsWith(route + "/")}
            nested
          />
        </NavLink>
      );
    });
    return template;
  };

  const renderCollapse = (collapses) => {
    if (!collapses) return null;
    return collapses.map(({ name, collapse, route, href, key, icon }) => {
      // Added icon
      let returnValue;
      // Handle logout if it's at this level (unlikely if it's under "brooklyn-alice" section)
      if (key === "logout") {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            icon={icon}
            onClick={() => {
              if (logout && typeof logout === "function") logout();
              navigate("/authentication/sign-in/basic");
            }}
          />
        );
      } else if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            icon={icon}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} icon={icon} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem
              color={color}
              name={name}
              icon={icon}
              active={key === itemName || pathname.startsWith(route + "/")}
            />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });
  };

  // MODIFIED: Use routesToDisplay
  const renderRoutes = routesToDisplay.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      let returnValue;

      // Handle Logout if it's a top-level 'collapse' type item
      if (type === "collapse" && key === "logout") {
        // Or any specific key for a top-level logout
        returnValue = (
          <SidenavCollapse
            key={key}
            name={name}
            icon={icon}
            noCollapse={noCollapse || true} // Logout is usually not collapsible
            onClick={() => {
              if (logout && typeof logout === "function") logout();
              navigate("/authentication/sign-in/basic");
            }}
          />
        );
      } else if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {/* Original template had 'collapse ? renderCollapse(collapse) : null' here,
                    but if noCollapse is true, it probably shouldn't render children.
                    If it can have children, they should be filtered.
                    Assuming 'collapse' array for 'noCollapse' items is already filtered or empty if not applicable.
                */}
                {/* {collapse ? renderCollapse(collapse) : null}  */}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return returnValue;
    }
  );

  if (authIsLoading) {
    return (
      <MDBox
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <MDTypography>Loading Sidenav...</MDTypography>
      </MDBox>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  // routes: PropTypes.arrayOf(PropTypes.object).isRequired, // MODIFIED: Removed routes from propTypes
};

export default Sidenav;
